import * as THREE from 'three';
import { TICK_HZ } from '@somnhold/content';
import { cellCenter, gustsAhead, panPulls, panWeights, ropeLive, type BattleState } from '@somnhold/sim';
import { simToWorld } from '@somnhold/render';
import { pinMark } from './mark-pin.ts';
import { t } from '../i18n.ts';
import { clear, h, setStyle } from '../ui/dom.ts';
import { ICONS } from '../ui/icons.ts';

/** Where a cover's «×» may sit round it (screen px), in order of preference. */
const OFFSETS: [number, number][] = [
  [26, 18],
  [-26, 18],
  [26, -26],
  [-26, -26],
  [0, 34],
];

/** Where the cut bridge's marks hang (render's ChasmPlace anchors, world points). */
export interface BridgeAnchors {
  hooks: THREE.Vector3[];
  pans: THREE.Vector3[];
  posts: THREE.Vector3[];
  chains: THREE.Vector3[];
  pile: THREE.Vector3 | null;
}

/** What the cut bridge's taps do: a mark (a rope, a cell, a piece to a pan) and the cover's aim on the board. */
export interface BridgeActions {
  mark: (at: number, to?: number) => void;
  /** The windbreak: the next tap on the board plants a cover there (on), or not (off). */
  aim: (on: boolean) => void;
  /** The ground's height under a sim point (the black rock's ledges and summit: the barbarian's chapter 5). */
  ground?: (x: number, y: number) => number;
}

/**
 * The cut bridge on screen (docs/sendas/guerrero.md, chapter 2), inside a path's own plate (game/senda-hud.ts) and over
 * the board:
 *
 * - the hooks: the span's winch as a bar; over each rope that bit, a mark to tap (the hero goes and cuts it) with its
 *   shakes as dots (one, two, three: the ogre's in red) and a ring running out until the first one climbs over;
 * - the windbreak: how far Kel is across; the gorge's flag, the next two gusts' sides and how soon; the covers in hand
 *   and «Clavar» (then a tap on the ground); over each planted cover a mark to pull it;
 * - the counterweight: the three pans as cards (the post's number, what pulls it now, level, sinking or rising, the
 *   pieces on it), the stretcher's pile; a piece tapped, then a pan, sends the hero. Over the board each pan's post
 *   and pull, the chains' numbers (what the middle passes out), and the rock cracking where the serpent comes up.
 *
 * Plain DOM written only when it changes (docs/RENDIMIENTO.md: «El HUD escribe texto y estilos solo si cambian»).
 */
export class BridgeHud {
  readonly panel: HTMLElement;
  private marks: HTMLElement;
  private v = new THREE.Vector3();
  private kind: 'hooks' | 'gust' | 'weight';
  // The hooks.
  private winchFill = h('i');
  private winchN = h('b.bh-n');
  private ropes = new Map<number, { el: HTMLButtonElement; ring: HTMLElement; at: number; gone: number }>();
  // The windbreak.
  private crossFill = h('i');
  private crossN = h('b.bh-n');
  private gusts: { el: HTMLElement; arrow: HTMLElement; when: HTMLElement }[] = [];
  private hand = h('span.bh-hand');
  private aimBtn: HTMLButtonElement | null = null;
  private aiming = false;
  private covers = new Map<number, HTMLButtonElement>();
  // The counterweight.
  private cards: { el: HTMLButtonElement; post: HTMLElement; pull: HTMLElement; state: HTMLElement; chips: HTMLElement; key: string }[] = [];
  private pileChips = h('div.bh-chips');
  private pileKey = '';
  private note = h('p.bh-note');
  private picked = -1;
  private panMarks: HTMLButtonElement[] = [];
  private chainMarks: HTMLElement[] = [];
  private crack = h('div.bh-crack', {}, h('span', { html: ICONS.skull }), h('b', {}, t('senda.bridge.crack')));

  constructor(
    s: BattleState,
    marks: HTMLElement,
    private act: BridgeActions,
  ) {
    this.marks = marks;
    const r = s.rules?.senda;
    this.kind = r?.hooks ? 'hooks' : r?.gust ? 'gust' : 'weight';
    if (this.kind === 'hooks') this.panel = this.buildHooks();
    else if (this.kind === 'gust') this.panel = this.buildGust(s);
    else this.panel = this.buildWeights(s);
  }

  /* ---------- the hooks ---------- */

  private buildHooks(): HTMLElement {
    return h(
      'div.bh.bhp-hooks',
      {},
      h('div.bh-row', {}, h('span.bh-ico', { html: ICONS.gear }), h('span.bh-label', {}, t('senda.bridge.span')), h('div.bar.bh-bar', {}, this.winchFill), this.winchN),
      h('div.bh-legend', {}, ...[1, 2, 3].map((n) => h(`span.bh-shakes.s${n}`, {}, h('span.bh-dots', {}, ...Array.from({ length: n }, () => h('i'))), t(`senda.bridge.shakes${n}`)))),
    );
  }

  private updateHooks(s: BattleState): void {
    const r = s.rules?.senda?.hooks;
    const w = s.senda?.hooks;
    if (!r || !w) return;
    const k = Math.min(1, w.wound / Math.max(1, r.winch));
    setStyle(this.winchFill, 'width', `${(k * 100).toFixed(1)}%`);
    const n = `${Math.floor(k * 100)} %`;
    if (this.winchN.textContent !== n) this.winchN.textContent = n;
    const now = performance.now();
    const seen = new Set<number>();
    for (const x of w.ropes) {
      if (!ropeLive(r, x)) continue;
      seen.add(x.i);
      let m = this.ropes.get(x.i);
      const p = r.plan[x.i];
      if (!p) continue;
      if (!m) {
        const ring = h('span.bh-ring');
        const el = h(
          `button.bh-rope.s${p.shakes}`,
          { type: 'button', 'data-test': `hook-${p.at}`, 'aria-label': t('senda.bridge.ropeHint'), onpointerup: (e: Event) => (e.stopPropagation(), this.act.mark(p.at)) },
          ring,
          h('span.bh-dots', {}, ...Array.from({ length: p.shakes }, () => h('i'))),
        ) as HTMLButtonElement;
        this.marks.append(el);
        m = { el, ring, at: p.at, gone: 0 };
        this.ropes.set(x.i, m);
      }
      // The ring: how long until the first one shows (then it stays full while the rest climb); cutting fills it red.
      const left = Math.max(0, p.t + p.warn - s.waveT);
      const k2 = x.by > 0 ? x.by / Math.max(1, r.cut) : left / Math.max(1, p.warn);
      m.ring.style.setProperty('--k', k2.toFixed(2));
      m.el.classList.toggle('cutting', x.by > 0);
      m.el.classList.toggle('out', left === 0);
      m.el.classList.toggle('sent', w.at === p.at);
    }
    for (const [i, m] of this.ropes) {
      if (seen.has(i)) continue;
      if (!m.gone) {
        m.gone = now;
        m.el.classList.add('gone');
      } else if (now - m.gone > 500) {
        m.el.remove();
        this.ropes.delete(i);
      }
    }
  }

  /* ---------- the windbreak ---------- */

  private buildGust(s: BattleState): HTMLElement {
    const g = s.rules?.senda?.gust;
    const flag = h('div.bh-flag');
    for (let i = 0; i < 2; i++) {
      const arrow = h('span.bh-arrow');
      const when = h('small');
      const el = h(`div.bh-gust${i ? '.then' : ''}`, {}, arrow, when);
      flag.append(el);
      this.gusts.push({ el, arrow, when });
    }
    this.aimBtn = h(
      'button.bh-aim',
      { type: 'button', 'data-test': 'cover-aim', onpointerup: (e: Event) => (e.stopPropagation(), this.setAim(!this.aiming)) },
      h('span', { html: ICONS.shield }),
      h('b', {}, t('senda.bridge.plant')),
    ) as HTMLButtonElement;
    void g;
    return h(
      'div.bh.bhp-gust',
      {},
      h('div.bh-row', {}, h('span.bh-ico', { html: ICONS.walk }), h('span.bh-label', {}, t('senda.bridge.cross')), h('div.bar.bh-bar', {}, this.crossFill), this.crossN),
      h('div.bh-row', {}, h('span.bh-ico', { html: ICONS.banner }), h('span.bh-label', {}, t('senda.bridge.flag')), flag),
      h('div.bh-row.bh-covers', {}, h('span.bh-ico', { html: ICONS.shield }), h('span.bh-label', {}, t('senda.bridge.covers')), this.hand, this.aimBtn),
      this.note,
    );
  }

  /** The cover's aim (the screen's next tap on the board plants one). */
  setAim(on: boolean): void {
    if (this.aiming === on) return;
    this.aiming = on;
    this.aimBtn?.classList.toggle('on', on);
    const text = on ? t('senda.bridge.plantHint') : '';
    if (this.note.textContent !== text) this.note.textContent = text;
    this.act.aim(on);
  }

  private updateGust(s: BattleState): void {
    const r = s.rules?.senda?.gust;
    const w = s.senda?.gust;
    if (!r || !w) return;
    const kel = s.senda?.npcs.find((n) => n.id === 'kel');
    const total = Math.hypot(r.to.x - r.from.x, r.to.y - r.from.y) - r.done;
    const k = kel ? Math.max(0, Math.min(1, 1 - (Math.hypot(r.to.x - kel.x, r.to.y - kel.y) - r.done) / Math.max(1, total))) : 0;
    setStyle(this.crossFill, 'width', `${(k * 100).toFixed(1)}%`);
    const n = `${Math.floor(k * 100)} %`;
    if (this.crossN.textContent !== n) this.crossN.textContent = n;
    // The flag: the next two gusts, their side and how soon; the first one red while it is coming.
    const next = gustsAhead(s, 2);
    this.gusts.forEach((g, i) => {
      const x = next[i];
      if (!x) {
        g.el.hidden = true;
        return;
      }
      g.el.hidden = false;
      const arrow = x.dir > 0 ? '→' : '←';
      if (g.arrow.textContent !== arrow) g.arrow.textContent = arrow;
      const secs = Math.max(0, Math.ceil((x.t - s.waveT) / TICK_HZ));
      const when = i === 0 ? (x.t - s.waveT <= r.warn ? t('senda.bridge.gustNow') : t('senda.bridge.gustIn', { s: secs })) : t('senda.bridge.then');
      if (g.when.textContent !== when) g.when.textContent = when;
      g.el.classList.toggle('warn', i === 0 && x.t - s.waveT <= r.warn);
    });
    // The covers in hand: planks, then the Company's shields.
    const html = `${Array.from({ length: r.covers - r.shields }, (_, i) => `<i class="plank${i < w.planks ? ' on' : ''}"></i>`).join('')}${Array.from({ length: r.shields }, (_, i) => `<i class="sh${i < w.shields ? ' on' : ''}">${ICONS.shield}</i>`).join('')}`;
    if (this.hand.innerHTML !== html) this.hand.innerHTML = html;
    const empty = w.planks + w.shields <= 0;
    if (this.aimBtn) this.aimBtn.disabled = empty || s.result !== 'running';
    if (empty && this.aiming) this.setAim(false);
    // A mark to pull each planted cover.
    const seen = new Set(w.cells);
    for (const c of w.cells) {
      if (this.covers.has(c)) continue;
      const el = h('button.bh-pull', { type: 'button', 'data-test': `cover-${c}`, 'aria-label': t('senda.bridge.pull'), onpointerup: (e: Event) => (e.stopPropagation(), this.act.mark(c)) }, h('span', { html: ICONS.close })) as HTMLButtonElement;
      this.marks.append(el);
      this.covers.set(c, el);
    }
    for (const [c, el] of this.covers) {
      if (seen.has(c)) {
        el.classList.toggle('sent', w.job === c);
        continue;
      }
      el.remove();
      this.covers.delete(c);
    }
  }

  /* ---------- the counterweight ---------- */

  private buildWeights(s: BattleState): HTMLElement {
    const r = s.rules?.senda?.weight;
    const grid = h('div.bh-pans');
    for (let i = 0; i < 3; i++) {
      const post = h('b.bh-post');
      const pull = h('small.bh-pull');
      const state = h('span.bh-state');
      const chips = h('div.bh-chips');
      const el = h(
        'button.bh-pan',
        { type: 'button', 'data-test': `pan-${i}`, onpointerup: (e: Event) => (e.stopPropagation(), this.toPan(i)) },
        h('span.bh-pan-name', {}, t(`senda.bridge.pan${i}`)),
        h('span.bh-pan-nums', {}, post, pull),
        state,
        chips,
      ) as HTMLButtonElement;
      grid.append(el);
      this.cards.push({ el, post, pull, state, chips, key: '' });
      // Over the board: the pan's post and pull (tap: here).
      const pm = h('button.bh-panmark', { type: 'button', 'data-test': `panmark-${i}`, onpointerup: (e: Event) => (e.stopPropagation(), this.toPan(i)) }, h('b'), h('small')) as HTMLButtonElement;
      this.marks.append(pm);
      this.panMarks.push(pm);
    }
    for (let i = 0; i < 2; i++) {
      const cm = h('div.bh-chain', {}, h('b'));
      this.marks.append(cm);
      this.chainMarks.push(cm);
    }
    this.marks.append(this.crack);
    void r;
    const back = h('button.bh-back', { type: 'button', 'data-test': 'pan-pile', onpointerup: (e: Event) => (e.stopPropagation(), this.toPan(3)) }, t('senda.bridge.back'));
    return h('div.bh.bhp-weight', {}, grid, h('div.bh-row.bh-pile', {}, h('span.bh-label', {}, t('senda.bridge.pile')), this.pileChips, back), this.note);
  }

  /** A piece tapped: picked (again: put down); with one picked, a pan sends it. */
  private pick(i: number): void {
    this.picked = this.picked === i ? -1 : i;
    this.pileKey = '';
    for (const c of this.cards) c.key = '';
  }

  private toPan(i: number): void {
    if (this.picked < 0) {
      const text = t('senda.bridge.pick');
      if (this.note.textContent !== text) this.note.textContent = text;
      this.note.classList.remove('flash');
      void this.note.offsetWidth;
      this.note.classList.add('flash');
      return;
    }
    this.act.mark(this.picked, i);
    this.picked = -1;
    this.pileKey = '';
    for (const c of this.cards) c.key = '';
  }

  private chip(s: BattleState, i: number, where: string): HTMLButtonElement {
    const r = s.rules?.senda?.weight;
    const p = r?.pieces[i];
    const stone = !!p?.stone;
    return h(
      `button.bh-chip${stone ? '.stone' : ''}${this.picked === i ? '.picked' : ''}${where === 'hand' ? '.hand' : ''}`,
      { type: 'button', 'data-test': `piece-${i}`, 'aria-label': stone ? t('senda.bridge.stone') : '', onpointerup: (e: Event) => (e.stopPropagation(), this.pick(i)) },
      stone ? h('span.bh-stone') : h('span.bh-shield', { html: ICONS.shield }),
      h('b', {}, String(p?.w ?? 0)),
    ) as HTMLButtonElement;
  }

  private updateWeights(s: BattleState): void {
    const r = s.rules?.senda?.weight;
    const w = s.senda?.weight;
    if (!r || !w) return;
    // In the hands: that one is picked.
    const hand = w.on.indexOf(-2);
    if (hand >= 0 && this.picked !== hand && w.job < 0) {
      this.picked = hand;
      this.pileKey = '';
      for (const c of this.cards) c.key = '';
    }
    if (this.picked >= 0 && (w.on[this.picked] === -3 || w.on[this.picked] === -4)) this.pick(this.picked);
    const pulls = panPulls(r, w);
    const ws = panWeights(r, w);
    this.cards.forEach((c, i) => {
      const post = r.posts[i] as number;
      const pull = pulls[i] as number;
      const key = `${post}|${pull}|${w.on.join(',')}|${this.picked}|${ws[i]}`;
      if (key === c.key) return;
      c.key = key;
      c.post.textContent = String(post);
      c.pull.textContent = t('senda.bridge.pulls', { n: pull });
      const st = pull === post ? 'level' : pull > post ? 'heavy' : 'light';
      c.el.classList.toggle('level', st === 'level');
      c.el.classList.toggle('heavy', st === 'heavy');
      c.el.classList.toggle('light', st === 'light');
      c.el.classList.toggle('target', this.picked >= 0);
      c.state.textContent = t(`senda.bridge.${st}`);
      clear(c.chips);
      w.on.forEach((o, k) => {
        if (o === i) c.chips.append(this.chip(s, k, 'pan'));
      });
      // The pan's mark over the board.
      const pm = this.panMarks[i];
      if (pm) {
        (pm.firstChild as HTMLElement).textContent = String(post);
        (pm.lastChild as HTMLElement).textContent = t('senda.bridge.pulls', { n: pull });
        pm.classList.toggle('level', st === 'level');
        pm.classList.toggle('heavy', st === 'heavy');
        pm.classList.toggle('light', st === 'light');
        pm.classList.toggle('target', this.picked >= 0);
      }
    });
    // The chains: what the middle passes to each outer one.
    const half = (ws[1] as number) >> 1;
    for (const cm of this.chainMarks) {
      const text = `+${half}`;
      const b = cm.firstChild as HTMLElement;
      if (b.textContent !== text) b.textContent = text;
      cm.classList.toggle('on', half > 0);
    }
    const pk = `${w.on.join(',')}|${this.picked}|${w.haul}`;
    if (pk !== this.pileKey) {
      this.pileKey = pk;
      clear(this.pileChips);
      w.on.forEach((o, k) => {
        if (o === -1 || o === -2) this.pileChips.append(this.chip(s, k, o === -2 ? 'hand' : 'pile'));
      });
      if (w.on.includes(-3)) this.pileChips.append(h('span.bh-haul', {}, t('senda.bridge.hanging')));
    }
    const shown = w.phase === 1;
    this.crack.classList.toggle('on', shown);
  }

  /* ---------- once a tick, every frame ---------- */

  update(s: BattleState): void {
    if (this.kind === 'hooks') this.updateHooks(s);
    else if (this.kind === 'gust') this.updateGust(s);
    else this.updateWeights(s);
  }

  place(s: BattleState, camera: THREE.Camera, w: number, ht: number, a: BridgeAnchors | null, heroes?: (THREE.Vector3 | undefined)[]): void {
    if (!a) return;
    if (this.kind === 'hooks') {
      const r = s.rules?.senda?.hooks;
      for (const [, m] of this.ropes) this.pin(m.el, a.hooks[m.at], camera, w, ht);
      void r;
    } else if (this.kind === 'gust') {
      // Each cover's «×» beside it, on the side clear of the heroes' name tags (on a phone it sat on the hero's name).
      const tags: number[] = [];
      for (const p of heroes ?? []) {
        if (!p) continue;
        this.v.copy(p).setY(p.y + 1.35).project(camera);
        tags.push((this.v.x * 0.5 + 0.5) * w, (-this.v.y * 0.5 + 0.5) * ht);
      }
      for (const [c, el] of this.covers) {
        const p = cellCenter(s.map.radius, c);
        simToWorld(p.x, p.y, this.v, 0.7).project(camera);
        if (this.v.z > 1) {
          setStyle(el, 'display', 'none');
          continue;
        }
        const x = (this.v.x * 0.5 + 0.5) * w;
        const y = (-this.v.y * 0.5 + 0.5) * ht;
        let bx = x + 26;
        let by = y + 16;
        for (const [dx, dy] of OFFSETS) {
          bx = x + dx;
          by = y + dy;
          let clear = true;
          for (let i = 0; i < tags.length; i += 2) if (Math.abs((tags[i] as number) - bx) < 52 && Math.abs((tags[i + 1] as number) - by) < 30) clear = false;
          if (clear) break;
        }
        setStyle(el, 'display', '');
        pinMark(el, bx, by);
      }
    } else {
      this.panMarks.forEach((el, i) => this.pin(el, a.posts[i] ?? a.pans[i], camera, w, ht));
      this.chainMarks.forEach((el, i) => this.pin(el, a.chains[i], camera, w, ht));
      const wt = s.senda?.weight;
      const r = s.rules?.senda?.weight;
      if (wt && r && wt.phase === 1 && wt.pan >= 0) {
        const p = r.pans[wt.pan] as { x: number; y: number };
        this.pin(this.crack, simToWorld(p.x + 750, p.y + 450, this.v, 0.5), camera, w, ht, true);
      }
    }
  }

  private pin(el: HTMLElement, p: THREE.Vector3 | null | undefined, camera: THREE.Camera, w: number, ht: number, copied = false): void {
    if (!p) {
      setStyle(el, 'display', 'none');
      return;
    }
    if (copied) this.v.project(camera);
    else this.v.copy(p).project(camera);
    if (this.v.z > 1) {
      setStyle(el, 'display', 'none');
      return;
    }
    setStyle(el, 'display', '');
    pinMark(el, (this.v.x * 0.5 + 0.5) * w, (-this.v.y * 0.5 + 0.5) * ht);
  }
}
